"use client";

import { useTranslations } from "next-intl";

import { Badge } from "@/shared/ui/data-display/badge";
import { translateUi } from "@/shared/lib/ui-translate";

interface Team {
  name: string;
  score?: number;
  seed?: number;
}

interface GameSide {
  team?: Team;
  score?: number;
}

interface Game {
  id: string;
  name: string;
  scheduled?: number;
  sides: GameSide[];
  games?: Game[];
}

interface TournamentBracketProps {
  game: Game;
}

export function TournamentBracket({ game }: TournamentBracketProps) {
  const tUi = useTranslations("Ui");
  const renderGame = (game: Game, level: number = 0) => {
    const isWinner = (side: GameSide, game: Game) => {
      if (side.score === undefined || game.sides[1]?.score === undefined)
        return false;
      return side.score > game.sides[1]?.score;
    };

    return (
      <div key={game.id} className="flex flex-col items-center">
        {/* Game Card */}
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 m-2 min-w-[200px] shadow-sm hover:shadow-md transition-shadow">
          <div className="text-xs text-gray-500 dark:text-gray-400 mb-3 text-center font-medium">
            {game.name}
          </div>

          {game.sides.map((side, index) => (
            <div
              key={index}
              className={`flex justify-between items-center p-2 rounded mb-1 ${
                isWinner(side, game)
                  ? "bg-green-100 dark:bg-green-900/20 border border-green-200 dark:border-green-800"
                  : "bg-gray-50 dark:bg-gray-700/50"
              }`}
            >
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-xs">
                  #{side.team?.seed || index + 1}
                </Badge>
                <span className="font-medium text-sm">
                  {side.team?.name || tUi("litTournamentBracket.komandaN", { n: index + 1 })}
                </span>
              </div>
              <span className="text-sm font-bold">
                {side.score !== undefined ? side.score : "-"}
              </span>
            </div>
          ))}
        </div>

        {/* Child Games */}
        {game.games && game.games.length > 0 && (
          <div className="flex gap-8 mt-4">
            {game.games.map((childGame) => renderGame(childGame, level + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="w-full overflow-x-auto">
      <div className="flex justify-center">{renderGame(game)}</div>
    </div>
  );
}

// Простые данные для турнирной сетки
export const sampleTournamentData: Game = {
  id: "final",
  get name() { return translateUi("litTournamentBracket.final"); },
  sides: [
    {
      team: { name: "Team Alpha", seed: 1 },
      score: 2,
    },
    {
      team: { name: "Team Beta", seed: 2 },
      score: 1,
    },
  ],
  games: [
    {
      id: "semi1",
      get name() { return translateUi("litTournamentBracket.polufinal1"); },
      sides: [
        {
          team: { name: "Team Alpha", seed: 1 },
          score: 2,
        },
        {
          team: { name: "Team Gamma", seed: 3 },
          score: 0,
        },
      ],
    },
    {
      id: "semi2",
      get name() { return translateUi("litTournamentBracket.polufinal2"); },
      sides: [
        {
          team: { name: "Team Beta", seed: 2 },
          score: 2,
        },
        {
          team: { name: "Team Delta", seed: 4 },
          score: 1,
        },
      ],
    },
  ],
};

// Данные для Winter Cup (id2)
export const winterCupData: Game = {
  id: "final",
  get name() { return translateUi("litTournamentBracket.final"); },
  sides: [
    {
      team: { name: "Winter Wolves", seed: 1 },
      score: 3,
    },
    {
      team: { name: "Ice Giants", seed: 2 },
      score: 1,
    },
  ],
  games: [
    {
      id: "semi1",
      get name() { return translateUi("litTournamentBracket.polufinal1"); },
      sides: [
        {
          team: { name: "Winter Wolves", seed: 1 },
          score: 2,
        },
        {
          team: { name: "Snow Leopards", seed: 3 },
          score: 0,
        },
      ],
    },
    {
      id: "semi2",
      get name() { return translateUi("litTournamentBracket.polufinal2"); },
      sides: [
        {
          team: { name: "Ice Giants", seed: 2 },
          score: 2,
        },
        {
          team: { name: "Frost Bears", seed: 4 },
          score: 1,
        },
      ],
    },
  ],
};
